import type { BadgeVariant } from '../components/symphony/ui/Badge';
import { type AgentTaskBucket, bucketContaining } from './agent-task-buckets';

// The components-sheet status-badge vocabulary. Every task-status badge shows its coarse BUCKET
// (Action needed / In progress / Completed / Failed) — label AND colour — derived from the single
// `bucketContaining` rule (mirrors native `AgentTaskBucket.badgeLabelKey` + `bucketChip`). So a PAUSED
// task reads "Action needed" and a CANCELLED task reads "Completed". The ONE exception is the task-DETAIL
// page (`taskStatusBadge(status, { detail: true })`), which shows IN_REVIEW as "In review".
//
// `TASK_STATUS_BADGE` below stays as the per-status variant SOURCE the tab pills + stat cards read for a
// specific bucket-representative status's colour; it is no longer a label source.
//
// It hands out a BADGE STYLE, never a colour. Each style owns its `--ds-badge-*`
// fill/ink/border triple in the token layer (see styles/design-tokens.css), so the
// render site is `<Badge variant={...}>` and nothing here knows a hex. This mirrors
// what `creditsProgress` did for the progress bars.
//
// `labelKey` is an i18n key (not literal text) — the label is localized at the render
// site via `t(badge.labelKey)`, mirroring the native TaskScreen.
/** The nine styles that own a `--ds-badge-*` fill/ink/border triple. The three date
 *  buckets and the WhatsApp group's mint `active` pill are deliberately outside it —
 *  they paint from the accent ramp, and nothing echoes them. */
export type TokenBackedBadgeVariant = Exclude<BadgeVariant, 'this-week' | 'next-week' | 'in-three-weeks' | 'active'>;

export interface TaskStatusBadge {
  labelKey: string;
  variant: TokenBackedBadgeVariant;
}

export const TASK_STATUS_BADGE: Record<string, TaskStatusBadge> = {
  ACTION_NEEDED: { labelKey: 'tasks.status.actionNeeded', variant: 'waiting-on-you' },
  PENDING: { labelKey: 'tasks.status.actionNeeded', variant: 'waiting-on-you' },
  IN_PROGRESS: { labelKey: 'tasks.status.inProgress', variant: 'in-progress' },
  // The v2 AgentTask reviewer stage — output being checked, still in motion.
  IN_REVIEW: { labelKey: 'tasks.status.inReview', variant: 'in-progress' },
  PAUSED: { labelKey: 'tasks.status.paused', variant: 'paused' },
  WAITING: { labelKey: 'tasks.status.waiting', variant: 'waiting-on-a-reply' },
  COMPLETED: { labelKey: 'tasks.status.done', variant: 'done' },
  // The v2 AgentTask terminal FAILURE — its own alert-red badge (matches native's
  // `failed` status); without this entry a failed task falls through to the
  // in-progress tint and reads as "In progress".
  FAILED: { labelKey: 'tasks.status.failed', variant: 'failed' },
  // The v2 AgentTask terminal cancellation — the quiet neutral badge.
  CANCELLED: { labelKey: 'tasks.status.cancelled', variant: 'waiting-on-a-reply' },
};

/** Bucket → badge {label, variant}. Every task-status badge shows this — the 4 buckets only. */
const BUCKET_BADGE: Record<AgentTaskBucket, TaskStatusBadge> = {
  waiting: { labelKey: 'tasks.status.actionNeeded', variant: 'waiting-on-you' },
  inProgress: { labelKey: 'tasks.status.inProgress', variant: 'in-progress' },
  done: { labelKey: 'tasks.status.done', variant: 'done' },
  failed: { labelKey: 'tasks.status.failed', variant: 'failed' },
};

/**
 * The task-status badge: its BUCKET's label + colour (the 4 buckets). `opts.detail` — the task-DETAIL
 * page ONLY — is the single exception: IN_REVIEW shows "In review" (still the in-progress blue), so a
 * reviewer stage is legible on the detail screen. Cards / goal-plan chips / tabs pass no `detail` and
 * get the plain bucket badge (IN_REVIEW → "In progress"). Mirrors native `taskDetailStatusLabel`.
 */
export function taskStatusBadge(status: string, opts?: { detail?: boolean }): TaskStatusBadge {
  if (opts?.detail && status === 'IN_REVIEW') {
    return { labelKey: 'tasks.status.inReview', variant: 'in-progress' };
  }
  return BUCKET_BADGE[bucketContaining(status)];
}

// A status DOT, a count pill on a tab, or a stat number is NOT a badge — the DS has no
// component for those — but each must match the badge the same status wears, or the app
// tells two stories about one task. These return that badge's own token, so the two can
// never drift apart. **Never** use them to paint something badge-shaped: that is `<Badge>`,
// and `test/badge-spec.test.ts` fails on a hand-rolled one.
//
// Spelled out rather than built by template, so `test/design-tokens.test.ts` can verify
// every one of these tokens really exists.
const STATUS_TOKENS: Record<TokenBackedBadgeVariant, { fill: string; ink: string }> = {
  'waiting-on-a-reply': { fill: 'var(--ds-badge-fill-waiting-on-a-reply)', ink: 'var(--ds-badge-text-waiting-on-a-reply)' },
  'waiting-on-you': { fill: 'var(--badge-waiting-on-you-bg)', ink: 'var(--badge-waiting-on-you-ink)' },
  'in-progress': { fill: 'var(--badge-in-progress-bg)', ink: 'var(--badge-in-progress-ink)' },
  done: { fill: 'var(--badge-done-bg)', ink: 'var(--badge-done-ink)' },
  paused: { fill: 'var(--badge-paused-bg)', ink: 'var(--badge-paused-ink)' },
  failed: { fill: 'var(--badge-failed-bg)', ink: 'var(--badge-failed-ink)' },
  connected: { fill: 'var(--ds-badge-fill-connected)', ink: 'var(--ds-badge-text-connected)' },
  agents: { fill: 'var(--ds-badge-fill-agents-group)', ink: 'var(--ds-text-primary)' },
  'agent-profession': { fill: 'var(--ds-badge-fill-agent-profession)', ink: 'var(--ds-badge-text-agent-profession)' },
};

export const statusFill = (variant: TokenBackedBadgeVariant): string => STATUS_TOKENS[variant].fill;
export const statusInk = (variant: TokenBackedBadgeVariant): string => STATUS_TOKENS[variant].ink;
